// ════════════════════════════════════════════════════════════════════
// nameChange.js — THE NAME CHANGE, sold at Bonnie's counter (the till is Pierre's alone)
// ════════════════════════════════════════════════════════════════════
// caP'n 2026-10-20: "The player can change it and can buy 1 'name change' item from the UP
// for 1 currency". Your NAME is the Empire-wide one on profiles/{uid} — every IE game and
// every Mark reads it — so ONE purchase here renames you everywhere. (The Command Center's
// own greeting name is separate and never touched; a Command Center place for this is pinned.)
//
// ⭐ ONE STEP: you type the new name, pay, and it's yours. Bought and used in the same
//   moment, so there is no stored "item" a later bug could duplicate or strand.
// ⭐ THE PRICE COMES FROM HERE (NAME_CHANGE_DC), never from the caller. The testing till
//   (markShop TESTING) charges ◆1 like every shelf; flip TESTING and the list price applies.
// ⭐ PURCHASE ORDER = markShop's: charge (a delta transaction that ABORTS, never clamps) →
//   write the name → if the write fails, REFUND by the same delta route and say so.
// ⭐ RECEIPT: games/unstable-pierre/nameChanges/{uid}/{auto} = { from, to, paidDC, listPriceDC,
//   testing, at } (the open games drawer, no rules deploy) — "stamp what was paid".
// ⚑ Friends' lists keep the old name on their side until that friend's list refreshes it
//   (ieSocial stores a name on each friend edge) — the Mark, chat and every game read the new one.
// ════════════════════════════════════════════════════════════════════
import { ref, get, update, push, runTransaction } from "firebase/database";
import { updateProfile } from "firebase/auth";
import { db, auth } from "./firebase";
import { TESTING, TESTING_PRICE_DC } from "./markShop";
import { cleanNewName, NAME_MAX } from "./nameRule.js";
export { NAME_MAX };

export const NAME_CHANGE_DC = 1;     // ⟵ DIAL the list price in ◆ (caP'n: 1 for now)
export const nameChangePrice = () => (TESTING ? TESTING_PRICE_DC : NAME_CHANGE_DC);

export async function buyNameChange(uid, raw) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you", message: "You have to be signed in as yourself to buy anything." };
  const profRef = ref(db, `profiles/${uid}`);
  const before = (await get(ref(db, `profiles/${uid}/name`)).catch(() => null))?.val() || "";
  const c = cleanNewName(raw, before);
  if (!c.ok) return { ok: false, reason: "bad_name", message: c.message };
  const cost = nameChangePrice();

  // ── THE CHARGE: a delta on the leaf that aborts rather than going negative ──
  let charged = 0;
  if (cost > 0) {
    let aborted = false, took = false;
    try {
      const res = await runTransaction(ref(db, `profiles/${uid}/divineCrests`), (cur) => {
        aborted = false; took = false;
        if (cur === null || cur === undefined) return cur;     // unloaded: re-run on the server's value
        if (cur < cost) { aborted = true; return; }
        took = true; return cur - cost;
      });
      if (aborted || !res.committed || !took) return { ok: false, reason: "insufficient", message: `A name change is ◆${cost}. Your purse is short.` };
      charged = cost;
    } catch (e) {
      return { ok: false, reason: "charge_failed", message: "The Crest wouldn't leave your hand. Try again." };
    }
  }

  // ── THE NAME: the two leaf fields every IE game reads (ieIdentity keeps them in lockstep) ──
  try {
    await update(profRef, { name: c.name, displayName: c.name });
  } catch (e) {
    if (charged > 0) {
      try { await runTransaction(ref(db, `profiles/${uid}/divineCrests`), (cur) => (cur || 0) + charged); }
      catch (e2) { return { ok: false, reason: "refund_failed", message: `Your name didn't change and ◆${charged} couldn't be returned on its own. Tell caP'n — nothing's lost, it just needs a hand.` }; }
    }
    return { ok: false, reason: "write_failed", message: "The ink smudged — your name didn't change and your Crest is back. Try again." };
  }
  if (auth.currentUser) updateProfile(auth.currentUser, { displayName: c.name }).catch(() => {});
  push(ref(db, `games/unstable-pierre/nameChanges/${uid}`), {
    from: String(before).slice(0, NAME_MAX), to: c.name, paidDC: charged, listPriceDC: NAME_CHANGE_DC, testing: TESTING || null, at: Date.now(),
  }).catch(() => {});
  return { ok: true, name: c.name, paidDC: charged };
}
